/**
 * Testimonial Grid module
 *
 * Two-up grid of color-theme testimonial panes
 */

// Deps
import { createComponent, FelaComponent } from "react-fela";
import { combineRules } from "fela";
import { colors } from "~/styleguide";
import { textStyles } from "~/styleguide/typography";
import { fluid } from "~/styleguide/utils";
import { getImageAspect } from "~/modules/images";
import get from "lodash.get";

// Components
import WordpressLink, {
    getLinkFromRelationship
} from "~/components/global/links";
import Visual from "~/components/shared/visual";

// Context
import { getThemeForModule } from "~/context/theme";

export default function TestimonialGrid({ block }) {
    const { entries } = block;

    return (
        <Container extend={testimonialWrapStyle} className="module-testimonial-grid">
            {entries &&
                entries.length &&
                entries.map((entry, i) => {
                    const {
                        customer_logo,
                        customer_photo,
                        theme_colors,
                        quote,
                        job_title,
                        link,
                        link_label,
                        link_type,
                        link_content,
                        name
                    } = entry;

                    let aspect = getImageAspect(customer_logo);
                    if (!aspect) aspect = 5;

                    const theme = getThemeForModule(theme_colors);

                    const linkInfo = getLinkFromRelationship(
                        link_type,
                        link_content,
                        link
                    );

                    let logo = get(
                        customer_logo,
                        ["sizes", "medium"],
                        get(customer_logo, "url")
                    );

                    let photo = get(
                        customer_photo,
                        ["sizes", "thumbnail"]
                    );
                    return (
                        <Testimonial
                            backgroundColor={theme.primary}
                            text={theme.text}
                            key={i}
                        >
                            {logo && (
                                <LogoContainer>
                                    <Visual
                                        aspect={aspect}
                                        background="contain"
                                        image={logo}
                                        extend={logoStyle}
                                    />
                                </LogoContainer>
                            )}

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    flexGrow: 1,
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            >
                                {quote && <Blockquote extend={textStyles.blockquoteSmall}
                                    dangerouslySetInnerHTML={{ __html: quote }}
                                />}
                                {name && (
                                    <FelaComponent as="div" style={sourceStyle}>
                                        {photo && (
                                            <Visual
                                                aspect={1}
                                                background="cover"
                                                height={60}
                                                image={photo}
                                                extend={photoStyle}
                                                width={60}
                                            />
                                        )}
                                        <FelaComponent as="div">
                                        <strong>{name}</strong>
                                        {job_title && (
                                            <span>{job_title}</span>
                                        )}
                                        </FelaComponent>
                                    </FelaComponent>
                                )}
                            </div>

                            {linkInfo.url && link_label && (
                                <WordpressLink
                                    url={linkInfo.url}
                                    slug={linkInfo.slug}
                                    label={link_label}
                                    extend={combineRules(
                                        textStyles.paragraphLarge,
                                        ctaStyle(theme.text)
                                    )}
                                />
                            )}
                        </Testimonial>
                    );
                })}
        </Container>
    );
}

const testimonialWrapStyle = {
    display: "flex",
    maxWidthTablet: {
        flexWrap: "wrap"
    }
};

const Container = createComponent(() => ({}));

const LogoContainer = createComponent(() => ({
    maxWidth: 200,
    display: "block",
    margin: "0 auto",
    width: "100%",

    extend: [fluid("margin-bottom", { max: 40, min: 20 })]
}));

const Blockquote = createComponent(() => ({
    maxWidth: 500,
    margin: "0 auto",
    display: "block",
    fontSize: "24px!important",
    lineHeight: "30px"
}), "div", ["dangerouslySetInnerHTML"]);

const Testimonial = createComponent(({ backgroundColor, text }) => ({
    width: "100%",
    minHeight: 400,
    padding: 50,
    background: backgroundColor,
    textAlign: "center",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "column",
    alignContent: "center",
    maxWidthTablet: {
        padding: "40px 50px",
        minHeight: 400
    },
    "& a": {
        width: "100%",
        marginTop: "auto",
        textAlign: "center"
    },
    extend: [
        {
            condition: text == "light",
            style: {
                color: colors.white
            }
        }
    ]
}));

const logoStyle = {
    display: "block",
    margin: "auto",
    minHeight: 24,
    maxHeight: 35,
    width: "100%",
    maxWidthMobile: {
        height: 30
    },
    "& .vv-align-middle": {
        display: "none"
    },
    "& .vv-visual": {
        height: "100%",
        width: "100%"
    },
    "& .vv-visual *": {
        height: "100%"
    }
};
const photoStyle = {
    display: "block",
    marginRight: 12,
    borderRadius: "50%",
    overflow: "hidden",
    maxWidthMobile: {
        height: 30,
        width: 30
    },
    "& .vv-align-middle": {
        display: "none"
    },
    "& .vv-visual": {
        height: "100%",
        width: "100%"
    },
    "& .vv-image": {
        display: "block",
        height: "100%",
        width: "100%"
    }
};

const sourceStyle = {
    maxWidth: 500,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 24,
    textAlign: "left",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    "& span": {
        display: "block",
        fontWeight: "400"
    }
};

const ctaStyle = text => ({
    display: "block",
    width: "100%",
    fontWeight: "bold",
    whenActive: {
        opacity: 0.8
    },
    extend: [
        fluid("margin-top", { max: 50, min: 36 }),
        {
            condition: text == "light",
            style: {
                color: colors.white
            }
        }
    ]
});
